import React, { useState, useEffect, Component } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import MobileMenu from './components/MobileMenu';
import SplashPage from './pages/SplashPage';
import HomePage from './pages/HomePage';
import PlayersPage from './pages/PlayersPage';
import CreatePlayerPage from './pages/CreatePlayerPage';
import MyTeamsPage from './pages/MyTeamsPage';
import TeamBuilderPage from './pages/TeamBuilderPage';
import LeaguesPage from './pages/LeaguesPage';
import LeagueDetailPage from './pages/LeagueDetailPage';
import GamesPage from './pages/GamesPage';
import AuthPage from './pages/AuthPage';
import SettingsPage from './pages/SettingsPage';
import { supabase, isSupabaseConfigured } from './services/supabase';
import ForgePage from './pages/ForgePage';
import CommissionerPage from './pages/CommissionerPage';
import PlayerProfilePage from './pages/PlayerProfilePage';
import TeamDetailPage from './pages/TeamDetailPage';
import { LeagueEntryPage } from './pages/LeagueEntryPage';
import PlayoffsPage from './pages/PlayoffsPage';
import FinancesPage from './pages/FinancesPage';
import QuickLeaguesPage from './pages/QuickLeaguesPage';
import QuickLeagueLobbyPage from './pages/QuickLeagueLobbyPage';
import QuickLeagueTeamPage from './pages/QuickLeagueTeamPage';
import TeamQueuePage from './pages/TeamQueuePage';
import FreeAgentsPage from './pages/FreeAgentsPage';
import BoostShopPage from './pages/BoostShopPage';
import MarketplacePage from './pages/MarketplacePage';
import EquipmentPage from './pages/EquipmentPage';
import ScrubMarketPage from './pages/ScrubMarketPage';
import OwnerRecordsPage from './pages/OwnerRecordsPage';
import DevelopmentReportPage from './pages/DevelopmentReportPage';
import DraftSheetPageWrapper from './pages/DraftSheetPageWrapper';
import FreeAgencyPageWrapper from './pages/FreeAgencyPageWrapper';
import DailyRewardPage from './pages/DailyRewardPage';
import HatcheryPage from './pages/HatcheryPage';
import QLMyTeamsPage from './pages/QLMyTeamsPage';
import './styles/globals.css';
import './pages/DraftFreeAgency.css';
import './App.css';

const App: React.FC = () => (
  <BrowserRouter>
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  </BrowserRouter>
);

class ErrorBoundary extends Component<{children: React.ReactNode}, {hasError: boolean; error: string}> {
  state = {hasError: false, error: ''};
  static getDerivedStateFromError(e: any) { return {hasError: true, error: e?.message || String(e)}; }
  render() {
    if (this.state.hasError) return (
      <div style={{padding:40,background:'#0a0e17',color:'#fff',minHeight:'100vh'}}>
        <h1 style={{color:'#ef4444'}}>💥 Something broke</h1>
        <pre style={{color:'#fbbf24',whiteSpace:'pre-wrap',fontSize:'0.85rem'}}>{this.state.error}</pre>
        <button onClick={() => window.location.reload()} style={{marginTop:20,padding:'10px 24px',borderRadius:8,border:'none',background:'#3b82f6',color:'#fff',cursor:'pointer',fontSize:'1rem'}}>Reload</button>
      </div>
    );
    return this.props.children;
  }
}

const AppInner: React.FC = () => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(window.innerWidth > 768);
  const navigate = useNavigate();

  // Auto-open sidebar on desktop, close on mobile resize
  useEffect(() => {
    const handleResize = () => setShowSidebar(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const location = useLocation();

  // Safety timeout: if auth check takes too long, stop loading
  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 5000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // Check for existing Supabase Auth session (persists across tabs/refreshes)
    if (isSupabaseConfigured()) {
      supabase.auth.getSession().then(async ({ data: { session } }) => {
        if (session?.user) {
          // Check if this is a password recovery session
          // Supabase puts type=recovery in the URL hash
          const isRecovery = window.location.hash.includes('type=recovery') ||
                             new URLSearchParams(window.location.search).get('mode') === 'reset';
          if (isRecovery) {
            // Don't auto-login — let AuthPage handle password reset
            // The session IS valid (needed for updateUser), but we don't set isSignedIn
            setLoading(false);
            navigate('/auth?mode=reset');
            return;
          }
          
          // Get user record from our users table
          const { data: userRecord } = await supabase.from('users').select('*').eq('id', session.user.id).single();
          if (userRecord) {
            setUserData(userRecord);
            setIsSignedIn(true);
            localStorage.setItem('userId', session.user.id);
            localStorage.setItem('userData', JSON.stringify(userRecord));
          }
        }
        setLoading(false);
      });

      // Listen for auth changes (sign in, sign out, token refresh, password recovery)
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          // User clicked password reset link — redirect to auth page in reset mode
          // Don't auto-login, let them set a new password first
          navigate('/auth?mode=reset');
          return;
        }
        if (event === 'SIGNED_IN') {
          // During password recovery, Supabase fires SIGNED_IN after PASSWORD_RECOVERY
          // Don't auto-login in that case
          const isRecoveryMode = new URLSearchParams(window.location.search).get('mode') === 'reset' ||
                                 window.location.pathname === '/auth';
          if (isRecoveryMode) return;
        }
        if (event === 'SIGNED_OUT') {
          setUserData(null);
          setIsSignedIn(false);
          localStorage.removeItem('userId');
          localStorage.removeItem('userData');
        }
      });

      return () => subscription.unsubscribe();
    } else {
      // Fallback: check localStorage (for dev without Supabase)
      const storedUser = localStorage.getItem('userData');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        setUserData(parsed);
        setIsSignedIn(true);
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setShowSidebar(false);
    // Refresh BALLS balance on navigation
    if (isSupabaseConfigured() && isSignedIn) {
      const userId = localStorage.getItem('userId');
      if (userId) {
        supabase.from('users').select('balls_balance').eq('id', userId).single()
          .then(({ data }) => {
            if (data) {
              setUserData((prev: any) => prev ? { ...prev, balls_balance: data.balls_balance } : prev);
            }
          });
      }
    }
  }, [location.pathname]);

  // Listen for custom navigation events from SplashPage
  useEffect(() => {
    const handleNavigate = (e: CustomEvent) => {
      if (e.detail) {
        const detail = e.detail;
        // Map splash page routes to actual routes
        const routeMap: Record<string, string> = {
          'create-player': '/create-player',
          'buy-team': '/my-teams',
          'leagues': '/leagues',
          'quick-leagues': '/quick-leagues',
          'ql-my-teams': '/ql-my-teams',
          'ql-top-owners': '/ql-top-owners',
          'auth': '/auth',
          'home': '/app',
        };
        // Handle boxscore links: navigate to the league page with game ID
        if (typeof detail === 'string' && detail.startsWith('boxscore-')) {
          const gameId = detail.replace('boxscore-', '');
          // Store game ID for league page to show boxscore modal
          sessionStorage.setItem('pendingBoxscore', gameId);
          navigate('/leagues');
          return;
        }
        // Handle team links: navigate to team detail
        if (typeof detail === 'string' && detail.startsWith('team-')) {
          const teamId = detail.replace('team-', '');
          navigate(`/team/${teamId}`);
          return;
        }
        navigate(routeMap[detail] || detail);
      }
    };
    window.addEventListener('navigate', handleNavigate as EventListener);
    return () => window.removeEventListener('navigate', handleNavigate as EventListener);
  }, [navigate]);

  const getActiveItem = (): string => {
    const path = location.pathname;
    if (path === '/' || path === '/app') return 'home';
    if (path === '/players') return 'agent-office';
    if (path === '/my-teams') return 'owner-office';
    if (path === '/free-agents') return 'free-agents';
    if (path === '/leagues') return 'traditional-leagues';
    if (path === '/quick-leagues') return 'current-leagues';
    if (path === '/quick-league-lobby') return 'ql-lobby';
    if (path === '/ql-my-teams') return 'ql-teams';
    if (path === '/ql-top-owners') return 'ql-top-owners';
    if (path === '/forge') return 'forge';
    if (path === '/marketplace') return 'marketplace';
    if (path === '/hatchery') return 'hatchery';
    if (path === '/equipment') return 'equipment';
    if (path === '/scrub-market') return 'scrub-market';
    if (path === '/ql-top-owners') return 'ql-top-owners';
    if (path === '/free-agents') return 'free-agents';
    if (path === '/daily-reward') return 'daily-reward';
    if (path === '/finances') return 'finances';
    return 'home';
  };

  const activeItem = getActiveItem();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  const isSplash = location.pathname === '/';

  return (
    <div className="app" style={isSplash ? { background: '#060a12' } : {
      backgroundImage: `linear-gradient(rgba(10, 14, 23, 0.75), rgba(10, 14, 23, 0.85)), url(/bg-outfield.jpg)`,
      backgroundSize: 'cover',
      backgroundAttachment: 'fixed',
      backgroundPosition: 'center',
    }}>
      <Header
        isSignedIn={isSignedIn}
        username={userData?.username}
        ballsBalance={userData?.balls_balance || 0}
        onMenuToggle={() => setShowSidebar(!showSidebar)}
        onSignIn={() => navigate('/auth')}
        onSignOut={async () => {
          await supabase.auth.signOut();
          localStorage.removeItem('userData');
          localStorage.removeItem('userId');
          setIsSignedIn(false);
          setUserData(null);
          navigate('/');
        }}
        isSplash={isSplash}
      />
      <MobileMenu
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        userData={userData}
        isSignedIn={isSignedIn}
        activeItem={activeItem}
        onNavigate={(route) => { 
          setShowSidebar(false); 
          navigate(route);
        }}
      />
      <div className="app-body">
        <Sidebar
          isOpen={showSidebar}
          onClose={() => setShowSidebar(false)}
          userData={userData}
          isSignedIn={isSignedIn}
          activeItem={activeItem}
          onNavigate={(route) => { 
            setShowSidebar(false); 
            navigate(route);
          }}
        />
        <main className={`main-content${isSplash ? ' splash-main' : ''}`}>
          <Routes>
            <Route path="/" element={<SplashPage />} />
            <Route path="/auth" element={<AuthPage onSuccess={(data) => { setUserData(data); setIsSignedIn(true); localStorage.setItem('userData', JSON.stringify(data)); navigate('/app'); }} />} />
            <Route path="/app" element={<HomePage isSignedIn={isSignedIn} userData={userData} />} />
            <Route path="/players" element={<PlayersPage onNavigate={navigate} onPlayerClick={(p) => navigate(`/player/${p.id}`)} />} />
            <Route path="/create-player" element={<CreatePlayerPage onNavigate={navigate} />} />
            <Route path="/my-teams" element={<MyTeamsPage onNavigate={navigate} userData={userData} />} />
            <Route path="/team-builder" element={<TeamBuilderPage onNavigate={navigate} userData={userData} />} />
            <Route path="/leagues" element={<LeaguesPage />} />
            <Route path="/league/:leagueId" element={<LeagueDetailPage onNavigate={navigate} userData={userData} />} />
            <Route path="/league/:leagueId/playoffs" element={<PlayoffsPage />} />
            <Route path="/games" element={<GamesPage />} />
            <Route path="/forge" element={<ForgePage />} />
            <Route path="/commissioner" element={<CommissionerPage userData={userData} />} />
            <Route path="/team-queue" element={<TeamQueuePage onNavigate={(page) => navigate(page)} />} />
            <Route path="/quick-leagues" element={<QuickLeaguesPage />} />
            <Route path="/quick-league-lobby" element={<QuickLeagueLobbyPage userData={userData} onNavigate={navigate} />} />
            <Route path="/ql-my-teams" element={<QLMyTeamsPage />} />
            <Route path="/ql-top-owners" element={<QuickLeaguesPage />} />
            <Route path="/ql-team/:leagueId/:teamId" element={<QuickLeagueTeamPage onNavigate={navigate} userData={userData} />} />
            <Route path="/finances" element={<FinancesPage userData={userData} />} />
            <Route path="/settings" element={<SettingsPage userData={userData} onUserDataUpdate={(data) => { setUserData(data); localStorage.setItem('userData', JSON.stringify(data)); }} />} />
            <Route path="/free-agents" element={<FreeAgentsPage onNavigate={navigate} userData={userData} />} />
            <Route path="/boost-shop" element={<BoostShopPage userData={userData} />} />
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/equipment" element={<EquipmentPage userData={userData} />} />
            <Route path="/scrub-market" element={<ScrubMarketPage onNavigate={navigate} userData={userData} />} />
            <Route path="/owner-records" element={<OwnerRecordsPage />} />
            <Route path="/league/:leagueId/dev-report" element={<DevelopmentReportPage />} />
            <Route path="/league/:leagueId/draft-sheet" element={<DraftSheetPageWrapper />} />
            <Route path="/league/:leagueId/free-agency" element={<FreeAgencyPageWrapper />} />
            <Route path="/player/:playerId" element={<PlayerProfilePage onBack={() => navigate(-1)} currentUserId={userData?.id} />} />
            <Route path="/team/:teamId" element={<TeamDetailPage onNavigate={navigate} currentUserId={userData?.id} />} />
            <Route path="/daily-reward" element={<DailyRewardPage userData={userData} />} />
            <Route path="/hatchery" element={<HatcheryPage onNavigate={navigate} userData={userData} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default App;
